import type { Recipient } from '@/types/messaging';

// ── Column Aliases (case-insensitive) ───────────────────────────────

const PHONE_ALIASES = [
  'phone', 'phone number', 'phone_number', 'mobile', 'cell',
  'telephone', 'number', 'mobile number', 'mobile_number',
  'contact number', 'contact_number', 'whatsapp', 'whatsapp number',
];

const NAME_ALIASES = [
  'name', 'full name', 'fullname', 'full_name', 'contact name',
  'contact_name', 'first name', 'first_name', 'customer name',
  'customer_name', 'customer',
];

const EMAIL_ALIASES = [
  'email', 'email address', 'email_address', 'e-mail', 'mail',
];

// ── CSV Parsing ─────────────────────────────────────────────────────

/**
 * Parse a single CSV line, handling quoted fields
 */
export function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);

  return result;
}

/**
 * Extract column headers from CSV content
 */
export function extractCSVHeaders(csvContent: string): string[] {
  const lines = csvContent.trim().split('\n');
  if (lines.length === 0) return [];
  return parseCSVLine(lines[0]).map((h) => h.trim());
}

/**
 * Auto-detect phone, name, email columns from headers (case-insensitive).
 * Returns original-case header names or null if not found.
 */
export function autoDetectColumns(headers: string[]): {
  phone: string | null;
  name: string | null;
  email: string | null;
} {
  const find = (aliases: string[]) => {
    const idx = headers.findIndex((h) => aliases.includes(h.toLowerCase().trim()));
    return idx >= 0 ? headers[idx] : null;
  };
  return {
    phone: find(PHONE_ALIASES),
    name: find(NAME_ALIASES),
    email: find(EMAIL_ALIASES),
  };
}

// ── Recipients Parsing ──────────────────────────────────────────────

/**
 * Parse a CSV string into recipients.
 *
 * @param csvContent  Raw CSV text (header + data rows)
 * @param columnMap   Optional explicit column mapping (original-case header names).
 *                    If omitted, columns are auto-detected from well-known aliases.
 */
export function parseCSVToRecipients(
  csvContent: string,
  columnMap?: { phone: string; name?: string; email?: string },
  options?: { propertyPrefix?: string; sourceTag?: string },
): Recipient[] {
  const lines = csvContent.trim().split('\n');
  if (lines.length < 2) return []; // Need at least header + 1 data row

  const rawHeaders = parseCSVLine(lines[0]).map((h) => h.trim());
  const headersLower = rawHeaders.map((h) => h.toLowerCase());

  // Resolve column indices — explicit mapping first, then auto-detect
  let phoneIndex: number;
  let nameIndex: number;
  let emailIndex: number;

  if (columnMap) {
    phoneIndex = rawHeaders.indexOf(columnMap.phone);
    nameIndex = columnMap.name ? rawHeaders.indexOf(columnMap.name) : -1;
    emailIndex = columnMap.email ? rawHeaders.indexOf(columnMap.email) : -1;
  } else {
    phoneIndex = headersLower.findIndex((h) => PHONE_ALIASES.includes(h));
    nameIndex = headersLower.findIndex((h) => NAME_ALIASES.includes(h));
    emailIndex = headersLower.findIndex((h) => EMAIL_ALIASES.includes(h));
  }

  if (phoneIndex === -1) {
    throw new Error('CSV must contain a "phone" or "phone number" column');
  }

  const prefix = options?.propertyPrefix ? `${options.propertyPrefix}-` : '';
  const tag = options?.sourceTag ? `${options.sourceTag}_` : '';
  const recipients: Recipient[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const phone = values[phoneIndex]?.trim();
    if (!phone) continue;

    // Build properties from ALL CSV columns for placeholder substitution
    // When propertyPrefix is set (e.g. "sheet-1"), keys become "sheet-1-name", "sheet-1-mobile", etc.
    const properties: Record<string, string> = {};
    headersLower.forEach((header, idx) => {
      const val = values[idx]?.trim();
      if (val) properties[`${prefix}${header}`] = val;
    });

    recipients.push({
      id: `csv_${tag}${i}_${Date.now()}`,
      name: nameIndex >= 0 ? values[nameIndex]?.trim() || 'Unknown' : 'Unknown',
      phone: normalizePhone(phone),
      email: emailIndex >= 0 ? values[emailIndex]?.trim() : undefined,
      source: 'csv',
      phoneFieldLabel: rawHeaders[phoneIndex] || undefined,
      properties,
    });
  }

  return recipients;
}

// ── Phone Normalization ─────────────────────────────────────────────

/**
 * Normalize a phone number (basic cleanup).
 * Only strips formatting characters — does NOT add country code prefix.
 * Country code is applied at send time by the message store.
 */
function normalizePhone(phone: string): string {
  // Handle scientific notation from Excel (e.g. "9.19877E+11" → "919877000000")
  if (/^\d+\.?\d*[eE]\+?\d+$/.test(phone.trim())) {
    const num = Number(phone);
    if (!isNaN(num)) {
      phone = num.toFixed(0);
    }
  }
  return phone.replace(/[\s\-()]/g, '');
}

// ── Export Utilities ─────────────────────────────────────────────────

/**
 * Convert recipients to CSV string for export
 */
export function recipientsToCSV(recipients: Recipient[]): string {
  const headers = 'Name,Phone,Email,Source\n';
  const rows = recipients
    .map((r) => `"${r.name}","${r.phone}","${r.email || ''}","${r.source}"`)
    .join('\n');
  return headers + rows;
}

/**
 * Download a CSV string as a file
 */
export function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}
